/**
 * SchedulingConfigCard — Scheduling configuration section extracted from HorariosTab.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CalendarClock } from "lucide-react";
import { ServicePeriodRulesCard } from "./ServicePeriodRulesCard";

export function SchedulingConfigCard({ scheduling, setScheduling }) {
  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="uppercase flex items-center gap-2"><CalendarClock className="w-5 h-5" />Programacion de Pedidos</CardTitle>
        <CardDescription>Configure como los clientes pueden agendar pedidos para despues.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Mode Selection */}
        <div className="space-y-2">
          <Label className="font-bold">Programacion</Label>
          <Select value={scheduling.mode} onValueChange={v => setScheduling(p => ({ ...p, mode: v }))}>
            <SelectTrigger className="bg-secondary border-transparent w-full md:w-80" data-testid="scheduling-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="disabled">Deshabilitar programaciones</SelectItem>
              <SelectItem value="allow">Permitir programaciones</SelectItem>
              <SelectItem value="forced">Forzar programaciones (siempre agendar)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {scheduling.mode === "disabled" && "Los clientes solo podran hacer pedidos en tiempo real cuando la tienda este abierta."}
            {scheduling.mode === "allow" && "Los clientes podran elegir entre recibir ahora o agendar para despues."}
            {scheduling.mode === "forced" && "Todos los pedidos seran programados. Util para tiendas que solo trabajan con agendamiento."}
          </p>
        </div>

        {scheduling.mode !== "disabled" && (
          <>
            {/* Allow when open */}
            <div className="flex items-center justify-between gap-4 p-3 bg-secondary/50 rounded-lg">
              <div>
                <Label className="font-medium">Permitir agendar cuando la tienda esta abierta</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Si desactivado, solo se permite agendar fuera del horario.</p>
              </div>
              <Switch
                checked={scheduling.allow_when_open}
                onCheckedChange={v => setScheduling(p => ({ ...p, allow_when_open: v }))}
                data-testid="scheduling-allow-when-open"
              />
            </div>

            {/* Hours Mode */}
            <div className="space-y-2">
              <Label className="font-bold">Horarios de programacion</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/30 transition">
                  <input type="radio" checked={scheduling.hours_mode === "business_hours"} onChange={() => setScheduling(p => ({ ...p, hours_mode: "business_hours" }))} className="accent-orange-500" />
                  <div>
                    <span className="text-sm font-medium">De acuerdo con el horario de abertura</span>
                    <p className="text-xs text-muted-foreground">Los horarios disponibles seguiran los turnos configurados arriba.</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-secondary/30 transition">
                  <input type="radio" checked={scheduling.hours_mode === "custom"} onChange={() => setScheduling(p => ({ ...p, hours_mode: "custom" }))} className="accent-orange-500" />
                  <div>
                    <span className="text-sm font-medium">De acuerdo con intervalos predefinidos</span>
                    <p className="text-xs text-muted-foreground">Los horarios seran generados automaticamente en intervalos regulares.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Configuration Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Anticipacion minima</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={scheduling.min_anticipation_hours} onChange={e => setScheduling(p => ({ ...p, min_anticipation_hours: Number(e.target.value) }))}
                    className="bg-secondary border-transparent w-20" min={0} data-testid="scheduling-anticipation" />
                  <span className="text-muted-foreground text-sm">Horas</span>
                </div>
                <p className="text-[11px] text-muted-foreground">Ej: si 2h, el cliente solo puede agendar 2 horas despues de ahora.</p>
              </div>
              <div className="space-y-2">
                <Label>Dias de agenda</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={scheduling.agenda_days} onChange={e => setScheduling(p => ({ ...p, agenda_days: Number(e.target.value) }))}
                    className="bg-secondary border-transparent w-20" min={1} max={60} data-testid="scheduling-agenda-days" />
                  <span className="text-muted-foreground text-sm">Dias</span>
                </div>
                <p className="text-[11px] text-muted-foreground">Cuantos dias en adelante el cliente puede agendar.</p>
              </div>
              <div className="space-y-2">
                <Label>Intervalo de programacion</Label>
                <Select value={String(scheduling.interval_minutes)} onValueChange={v => setScheduling(p => ({ ...p, interval_minutes: Number(v) }))}>
                  <SelectTrigger className="bg-secondary border-transparent" data-testid="scheduling-interval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 Minutos</SelectItem>
                    <SelectItem value="30">30 Minutos</SelectItem>
                    <SelectItem value="45">45 Minutos</SelectItem>
                    <SelectItem value="60">60 Minutos</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Las opciones seran intervalos de este valor.</p>
              </div>
            </div>
          </>
        )}
      </CardContent>
      <ServicePeriodRulesCard scheduling={scheduling} setScheduling={setScheduling} />
    </Card>
  );
}
